import { themedConfirm } from '../../lib/themed-dialogs';
// WebHost — shared web hosting: nginx + PHP-FPM, víc webů na jednom kontejneru
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Modal,
  PageHeader,
  PublishPanel,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../../components/ui';
import {
  GitBranch,
  Globe,
  LayoutGrid,
  Loader2,
  Play,
  Plus,
  RotateCw,
  Server,
  Square,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ────────────────────────────────────────────────────────────────────

interface Instance {
  name: string;
  status: string;
  port: number;
  php_version: string;
  sites: number;
  domains: string[];
}

interface Site {
  name: string;
  type: string;
  domain: string | null;
  status: string;
  git_repo: string | null;
  git_branch?: string;
}

interface InstanceDetail {
  instance: Instance & { container: string; memory_limit: string };
  sites: Site[];
}

// ── Site type config ─────────────────────────────────────────────────────────

const SITE_TYPE_CFG: Record<string, { label: string; color: string }> = {
  static: { label: 'Static', color: 'bg-gray-600' },
  php: { label: 'PHP', color: 'bg-purple-600' },
  wordpress: { label: 'WordPress', color: 'bg-blue-600' },
};

// ── Create Instance Modal ────────────────────────────────────────────────────

function CreateInstanceModal({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [memLimit, setMemLimit] = useState('512m');

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string; port?: number }>(
        'webhost.create',
        { name, memory_limit: memLimit },
      ),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('webhost.toastInstanceCreated', { name, port: r.port }));
        onCreated();
      } else toast.error(r.error ?? t('webhost.error'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (!open) return null;
  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  return (
    <Modal open={open} onClose={onClose} title={t('webhost.newInstanceTitle')}>
      <div className='space-y-4'>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('webhost.nameSlug')}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder={t('webhost.instanceNamePlaceholder')}
            className={inp}
            autoFocus
          />
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('webhost.memoryLimit')}</label>
          <input
            value={memLimit}
            onChange={(e) => setMemLimit(e.target.value)}
            placeholder='512m'
            className={inp}
          />
        </div>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('webhost.cancel')}</Button>
          <Button
            variant='primary'
            loading={mut.isPending}
            onClick={() => mut.mutate()}
            disabled={name.length < 2}
          >
            {t('webhost.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Add Site Modal ───────────────────────────────────────────────────────────

function AddSiteModal({ open, onClose, instanceName, onCreated }: {
  open: boolean;
  onClose: () => void;
  instanceName: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [siteType, setSiteType] = useState<string>('static');
  const [domain, setDomain] = useState('');
  const [gitRepo, setGitRepo] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; error?: string }>('webhost.site_create', {
        instance: instanceName,
        name,
        site_type: siteType,
        ...(domain ? { domain } : {}),
        ...(gitRepo ? { git_repo: gitRepo } : {}),
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('webhost.toastSiteAdded', { name }));
        onCreated();
      } else toast.error(r.error ?? t('webhost.error'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (!open) return null;
  const inp =
    'w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';
  const opt = (sel: boolean) =>
    `p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
      sel
        ? 'border-blue-500 bg-blue-500/10 text-white'
        : 'border-gray-800 text-gray-500 hover:border-gray-600'
    }`;

  return (
    <Modal open={open} onClose={onClose} title={t('webhost.newSiteTitle')}>
      <div className='space-y-4'>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('webhost.nameSlug')}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder={t('webhost.siteNamePlaceholder')}
            className={inp}
            autoFocus
          />
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-2 block'>{t('webhost.type')}</label>
          <div className='grid grid-cols-3 gap-2'>
            {(['static', 'php', 'wordpress'] as const).map((tp) => (
              <div
                key={tp}
                className={opt(siteType === tp)}
                onClick={() => setSiteType(tp)}
              >
                <div className='text-sm font-medium'>{SITE_TYPE_CFG[tp].label}</div>
                <div className='text-[10px] text-gray-600 mt-0.5'>
                  {tp === 'static'
                    ? 'HTML / React build'
                    : tp === 'php'
                    ? 'PHP + nginx'
                    : 'WP + MariaDB'}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('webhost.domainOptional')}</label>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder='web.example.cz'
            className={inp}
          />
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('webhost.gitRepoOptional')}</label>
          <input
            value={gitRepo}
            onChange={(e) => setGitRepo(e.target.value)}
            placeholder='https://github.com/...'
            className={inp}
          />
        </div>
        <div className='flex justify-end gap-2'>
          <Button variant='ghost' onClick={onClose}>{t('webhost.cancel')}</Button>
          <Button
            variant='primary'
            loading={mut.isPending}
            onClick={() => mut.mutate()}
            disabled={name.length < 2}
          >
            {t('webhost.addSite')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Instance Detail ──────────────────────────────────────────────────────────

function InstanceView({ name }: { name: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['webhost-detail', name],
    queryFn: () =>
      api.mcp<{ ok: boolean } & InstanceDetail>('webhost.status', { name }),
    refetchInterval: 10_000,
  });
  const [showAddSite, setShowAddSite] = useState(false);

  const manageMut = useMutation({
    mutationFn: (action: string) => api.mcp('webhost.manage', { name, action }),
    onSuccess: () => {
      toast.success(t('webhost.ok'));
      qc.invalidateQueries({ queryKey: ['webhost-detail', name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deployMut = useMutation({
    mutationFn: (siteName: string) =>
      api.mcp<{ ok: boolean; output?: string; error?: string }>(
        'webhost.site_deploy',
        { instance: name, name: siteName },
      ),
    onSuccess: (r) => {
      if (r.ok) toast.success(r.output ?? t('webhost.deployOk'));
      else toast.error(r.error ?? t('webhost.deployFailed'));
      qc.invalidateQueries({ queryKey: ['webhost-detail', name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteSiteMut = useMutation({
    mutationFn: (siteName: string) =>
      api.mcp<{ ok: boolean }>('webhost.site_delete', { instance: name, name: siteName }),
    onSuccess: () => {
      toast.success(t('webhost.toastSiteDeleted'));
      qc.invalidateQueries({ queryKey: ['webhost-detail', name] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  if (isLoading || !data?.ok) {
    return (
      <div className='py-8 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />{t('webhost.loading')}
      </div>
    );
  }

  const inst = data.instance;
  const sites = data.sites;
  const running = inst.status === 'running';

  return (
    <div className='space-y-4'>
      {/* Server visual — kontejner jako obdélník, weby uvnitř */}
      <div
        className={`rounded-2xl border-2 p-4 transition-colors ${
          running
            ? 'border-green-500/30 bg-gradient-to-b from-green-500/5 to-transparent'
            : 'border-gray-700 bg-gray-900/30'
        }`}
      >
        {/* Header — instance info */}
        <div className='flex items-center gap-3 mb-4 pb-3 border-b border-gray-800/50'>
          <div className={`w-3 h-8 rounded-full ${running ? 'bg-green-500' : 'bg-gray-600'}`} />
          <div className='flex-1'>
            <div className='flex items-center gap-2'>
              <Server size={16} className={running ? 'text-green-400' : 'text-gray-500'} />
              <span className='text-sm font-bold text-white'>{inst.name}</span>
              <span className='text-[10px] font-mono text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded'>
                {inst.container}
              </span>
            </div>
            <div className='text-[10px] text-gray-500 mt-0.5'>
              {t('webhost.instanceMeta', {
                port: inst.port,
                php: inst.php_version,
                memory: inst.memory_limit,
              })} · {t('webhost.siteCount', { count: sites.length })}
            </div>
          </div>
          <div className='flex gap-1'>
            {!running && (
              <Button
                variant='success'
                size='sm'
                loading={manageMut.isPending}
                onClick={() => manageMut.mutate('start')}
              >
                <Play size={12} />
              </Button>
            )}
            {running && (
              <Button
                variant='warning'
                size='sm'
                loading={manageMut.isPending}
                onClick={() => manageMut.mutate('stop')}
              >
                <Square size={12} />
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              loading={manageMut.isPending}
              onClick={() => manageMut.mutate('restart')}
            >
              <RotateCw size={12} />
            </Button>
          </div>
        </div>

        {/* Sites inside — bloky uvnitř kontejneru */}
        {sites.length === 0
          ? (
            <div className='py-8 text-center'>
              <Globe size={24} className='text-gray-700 mx-auto mb-2' />
              <p className='text-xs text-gray-600 mb-3'>
                {t('webhost.emptyContainer')}
              </p>
              <Button variant='primary' size='sm' onClick={() => setShowAddSite(true)}>
                <Plus size={12} /> {t('webhost.addSite')}
              </Button>
            </div>
          )
          : (
            <div className='grid gap-2'>
              {sites.map((site) => {
                const cfg = SITE_TYPE_CFG[site.type] ?? SITE_TYPE_CFG.static;
                const active = site.status === 'active';
                return (
                  <div
                    key={site.name}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      active
                        ? 'border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/50'
                        : 'border-red-500/20 bg-red-500/5'
                    }`}
                  >
                    <div
                      className={`w-1.5 h-10 rounded-full flex-shrink-0 ${
                        active
                          ? (site.type === 'wordpress'
                            ? 'bg-blue-500'
                            : site.type === 'php'
                            ? 'bg-purple-500'
                            : 'bg-gray-500')
                          : 'bg-red-500'
                      }`}
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-semibold text-white'>{site.name}</span>
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded text-white font-bold uppercase tracking-wider ${cfg.color}`}
                        >
                          {cfg.label}
                        </span>
                      </div>
                      <div className='flex items-center gap-2 mt-0.5'>
                        {site.domain && (
                          <span className='text-[11px] text-gray-400 font-mono'>{site.domain}</span>
                        )}
                        {site.git_repo && (
                          <span className='text-[10px] text-gray-600 flex items-center gap-1'>
                            <GitBranch size={8} />{' '}
                            {site.git_repo.split('/').pop()?.replace('.git', '')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className='flex gap-1 flex-shrink-0'>
                      {site.git_repo && (
                        <Button
                          variant='ghost'
                          size='sm'
                          loading={deployMut.isPending}
                          onClick={() => deployMut.mutate(site.name)}
                          title={t('webhost.gitDeploy')}
                        >
                          <Upload size={11} />
                        </Button>
                      )}
                      <Button
                        variant='danger'
                        size='sm'
                        onClick={async () => await themedConfirm(t('webhost.confirmDeleteSite', { name: site.name })) &&
                          deleteSiteMut.mutate(site.name)}
                        title={t('webhost.delete')}
                      >
                        <Trash2 size={11} />
                      </Button>
                    </div>
                  </div>
                );
              })}
              <button
                onClick={() => setShowAddSite(true)}
                className='flex items-center justify-center gap-2 p-2.5 rounded-xl border-2 border-dashed border-gray-800 text-gray-600 text-xs hover:border-gray-600 hover:text-gray-400 transition-colors'
              >
                <Plus size={12} /> {t('webhost.addSite')}
              </button>
            </div>
          )}
      </div>

      {/* Publish — veřejný přístup ke sdílenému hostingu */}
      <Card>
        <CardContent className='p-4'>
          <div className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'>
            {t('webhost.publicAccess')}
          </div>
          <PublishPanel
            module='webhost'
            instance={inst.name}
            port={inst.port}
            onChanged={() => qc.invalidateQueries({ queryKey: ['webhost-detail', name] })}
          />
        </CardContent>
      </Card>

      <AddSiteModal
        open={showAddSite}
        onClose={() => setShowAddSite(false)}
        instanceName={name}
        onCreated={() => {
          setShowAddSite(false);
          qc.invalidateQueries({ queryKey: ['webhost-detail', name] });
        }}
      />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function WebHostPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['webhost-list'],
    queryFn: () =>
      api.mcp<{ ok: boolean; instances: Instance[]; total: number }>('webhost.list', {}),
    refetchInterval: 15_000,
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => api.mcp('webhost.delete', { name, delete_data: true }),
    onSuccess: () => {
      toast.success(t('webhost.toastInstanceDeleted'));
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['webhost-list'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const instances = data?.instances ?? [];
  const totalSites = instances.reduce((a, i) => a + i.sites, 0);
  const runningCount = instances.filter((i) => i.status === 'running').length;

  return (
    <div className='space-y-6'>
      <PageHeader
        title='WebHost'
        description={t('webhost.subtitle')}
        icon={LayoutGrid}
        actions={
          <Button variant='primary' size='sm' onClick={() => setShowCreate(true)}>
            <Plus size={14} /> {t('webhost.newInstance')}
          </Button>
        }
      />

      {isLoading && (
        <div className='py-12 text-center text-gray-500'>
          <Loader2 size={16} className='animate-spin inline mr-2' />{t('webhost.loading')}
        </div>
      )}

      {!isLoading && instances.length === 0 && (
        <EmptyState
          icon={<LayoutGrid size={40} />}
          title={t('webhost.noInstancesTitle')}
          description={t('webhost.noInstancesDesc')}
          action={
            <Button variant='primary' onClick={() => setShowCreate(true)}>
              {t('webhost.createFirstInstance')}
            </Button>
          }
        />
      )}

      {instances.length > 0 && (
        <>
          <StatsGrid columns={3}>
            <StatCard label={t('webhost.statInstances')} value={instances.length} icon={Server} />
            <StatCard label={t('webhost.statSites')} value={totalSites} icon={Globe} />
            <StatCard label={t('webhost.statRunning')} value={runningCount} icon={Play} />
          </StatsGrid>

          <div className='grid grid-cols-1 xl:grid-cols-3 gap-4'>
            <div className='xl:col-span-1 space-y-2'>
              {instances.map((inst) => (
                <div
                  key={inst.name}
                  onClick={() => setSelected(inst.name)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${
                    selected === inst.name
                      ? 'border-blue-500/50 bg-blue-950/10'
                      : 'border-gray-800 hover:border-gray-600'
                  }`}
                >
                  <div className='flex items-center gap-2 mb-1'>
                    <StatusDot status={inst.status === 'running' ? 'running' : 'stopped'} />
                    <span className='text-sm font-semibold text-white'>{inst.name}</span>
                    <span className='text-[10px] text-gray-600 ml-auto'>:{inst.port}</span>
                  </div>
                  <div className='text-xs text-gray-500'>
                    {t('webhost.siteCount', { count: inst.sites })} ·{' '}
                    {inst.domains.length > 0 ? inst.domains.join(', ') : t('webhost.noDomain')}
                  </div>
                  <div className='mt-2 flex justify-end'>
                    <Button
                      variant='danger'
                      size='sm'
                      onClick={async (e) => {
                        e.stopPropagation();
                        await themedConfirm(t('webhost.confirmDeleteInstance', { name: inst.name })) &&
                          deleteMut.mutate(inst.name);
                      }}
                    >
                      <Trash2 size={10} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className='xl:col-span-2'>
              {selected
                ? <InstanceView name={selected} />
                : (
                  <div className='py-16 text-center text-gray-600 text-sm'>
                    <Server size={24} className='mx-auto mb-2 text-gray-700' />
                    {t('webhost.selectInstance')}
                  </div>
                )}
            </div>
          </div>
        </>
      )}

      <CreateInstanceModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          qc.invalidateQueries({ queryKey: ['webhost-list'] });
        }}
      />
    </div>
  );
}
